import React, { useMemo, useState } from "react";
import axiosInstance from "@/lib/api";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Mail, User, Lock, ShieldCheck } from "lucide-react";

const strengthLabel = (score: number) => {
  if (score >= 4) return "Strong";
  if (score === 3) return "Good";
  if (score === 2) return "Fair";
  if (score === 1) return "Weak";
  return "Very weak";
};

const scorePassword = (pwd: string) => {
  let s = 0;
  if (!pwd) return 0;
  if (pwd.length >= 8) s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[a-z]/.test(pwd)) s++;
  if (/\d/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++; // special char
  return Math.min(s, 5);
};

type Step = "details" | "otp";

const RegisterPage: React.FC = () => {
  const [step, setStep] = useState<Step>("details");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otpCode, setOtpCode] = useState(""); // NEW
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const pwdScore = useMemo(() => scorePassword(password), [password]);
  const pwdOk = password.length >= 8 && pwdScore >= 3; // minimal gate
  const confirmOk = confirm.length > 0 && confirm === password;

  const handleStartRegistration = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please fill in your name and email.");
      return;
    }

    if (!pwdOk) {
      toast.error(
        "You cannot create an account with a weak password. Use at least 8 characters and include a mix of uppercase/lowercase, number, or symbol."
      );
      return;
    }

    if (!confirmOk) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post("/users/register/start", {
        name: name.trim(),
        email: email.trim(),
        password,
      });

      toast.success(
        "Step 1 complete. The OTP has been sent to the system admin. Contact the admin to get the code."
      );
      setStep("otp");
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        toast.error(err.response?.data?.message || "Registration failed");
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!email.trim()) {
      toast.error("Email is missing. Please restart registration.");
      return;
    }
    if (!otpCode.trim()) {
      toast.error("Please enter the OTP provided by the admin.");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post("/users/register/verify", {
        email: email.trim(),
        code: otpCode.trim(),
      });

      toast.success("Registration verified! You can now log in.");
      navigate("/login");
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        toast.error(err.response?.data?.message || "OTP verification failed");
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === "details") {
      void handleStartRegistration();
    } else {
      void handleVerifyOtp();
    }
  };

  const isDetailsStep = step === "details";
  const ADMIN_EMAIL_HINT =
    import.meta.env.VITE_ADMIN_OTP_EMAIL || "the system administrator";

  return (
    <div className="flex items-center justify-center w-full min-h-screen px-4 py-12 bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-blue-900">
            {isDetailsStep ? "Create your account" : "Verify admin OTP"}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {isDetailsStep
              ? "Join Embrace Admin to manage your fleet and bookings."
              : "Ask the admin for the OTP that was emailed to them and enter it below to finish registration."}
          </p>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mt-3 text-xs font-medium text-gray-600">
            <span
              className={`px-2 py-0.5 rounded-full ${
                isDetailsStep
                  ? "bg-blue-900 text-white"
                  : "bg-blue-100 text-blue-900"
              }`}
            >
              1. Details
            </span>
            <span>→</span>
            <span
              className={`px-2 py-0.5 rounded-full ${
                !isDetailsStep
                  ? "bg-blue-900 text-white"
                  : "bg-blue-100 text-blue-900"
              }`}
            >
              2. Admin OTP
            </span>
          </div>
        </div>

        {/* Card */}
        <form
          onSubmit={handleSubmit}
          className="relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl shadow-[0_10px_40px_-10px_rgba(30,64,175,0.25)] p-6 sm:p-7 transition-transform"
        >
          {/* Info banner for admin-based registration */}
          <div className="flex items-start gap-3 p-3 mb-4 text-xs border border-blue-100 rounded-lg bg-blue-50/70">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-700" />
            <p className="text-[11px] leading-relaxed text-gray-700">
              This is an{" "}
              <span className="font-semibold text-blue-900">
                internal system
              </span>
              . When you submit your details, a one-time code is sent to{" "}
              <span className="font-semibold">{ADMIN_EMAIL_HINT}</span>. Only
              users approved by the admin will receive the OTP and complete
              registration.
            </p>
          </div>

          {/* Name */}
          <label className="block mb-1 text-sm font-semibold text-blue-900">
            Full Name
          </label>
          <div className="relative mb-4">
            <span className="absolute inset-y-0 flex items-center pointer-events-none left-3">
              <User className="w-4 h-4 text-gray-500" />
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isDetailsStep}
              className="w-full rounded-lg border border-gray-300 bg-white/80 px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter your full name"
              autoComplete="name"
            />
          </div>

          {/* Email */}
          <label className="block mb-1 text-sm font-semibold text-blue-900">
            Email
          </label>
          <div className="relative mb-4">
            <span className="absolute inset-y-0 flex items-center pointer-events-none left-3">
              <Mail className="w-4 h-4 text-gray-500" />
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!isDetailsStep} // lock email after step 1
              className="w-full rounded-lg border border-gray-300 bg-white/80 px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter your email"
              autoComplete="email"
              inputMode="email"
            />
          </div>

          {/* Password + Confirm only in step 1 */}
          {isDetailsStep && (
            <>
              {/* Password */}
              <label className="block mb-1 text-sm font-semibold text-blue-900">
                Password
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 flex items-center pointer-events-none left-3">
                  <Lock className="w-4 h-4 text-gray-500" />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white/80 px-10 py-2.5 pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 flex items-center px-2 text-gray-600 right-2"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Strength meter */}
              <div className="mt-2 mb-4">
                <div className="h-1.5 w-full rounded bg-gray-200 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      pwdScore <= 1
                        ? "bg-red-500"
                        : pwdScore === 2
                        ? "bg-orange-500"
                        : pwdScore === 3
                        ? "bg-yellow-500"
                        : pwdScore === 4
                        ? "bg-green-500"
                        : "bg-emerald-600"
                    }`}
                    style={{ width: `${(pwdScore / 5) * 100}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-600">
                  Strength:{" "}
                  <span className="font-medium">{strengthLabel(pwdScore)}</span>{" "}
                  <span className="text-gray-500">
                    (min 8 chars, mix of cases, number, or symbol)
                  </span>
                </p>
              </div>

              {/* Confirm Password */}
              <label className="block mb-1 text-sm font-semibold text-blue-900">
                Confirm Password
              </label>
              <div className="relative mb-5">
                <span className="absolute inset-y-0 flex items-center pointer-events-none left-3">
                  <Lock className="w-4 h-4 text-gray-500" />
                </span>
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={`w-full rounded-lg border bg-white/80 px-10 py-2.5 pr-11 focus:outline-none focus:ring-2 ${
                    confirm.length === 0
                      ? "border-gray-300 focus:ring-blue-500"
                      : confirm === password
                      ? "border-green-400 focus:ring-green-500"
                      : "border-red-300 focus:ring-red-500"
                  }`}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute inset-y-0 flex items-center px-2 text-gray-600 right-2"
                  aria-label={
                    showConfirm
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                {confirm.length > 0 && confirm !== password && (
                  <p className="mt-1 text-xs text-red-600">
                    Passwords do not match.
                  </p>
                )}
              </div>
            </>
          )}

          {/* OTP field only in step 2 */}
          {!isDetailsStep && (
            <>
              <label className="block mb-1 text-sm font-semibold text-blue-900">
                Admin OTP Code
              </label>
              <div className="relative mb-5">
                <span className="absolute inset-y-0 flex items-center pointer-events-none left-3">
                  <ShieldCheck className="w-4 h-4 text-gray-500" />
                </span>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white/80 px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter the OTP given by the admin"
                  inputMode="numeric"
                />
                <p className="mt-1 text-xs text-gray-500">
                  The OTP was sent to the system administrator’s email. Only
                  users approved by the admin can complete registration.
                </p>
              </div>
            </>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="group relative inline-flex w-full items-center justify-center rounded-lg bg-blue-900 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-800 disabled:opacity-60"
          >
            <span className="absolute inset-0 rounded-lg -z-10 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 blur-xl group-hover:from-blue-500/20 group-hover:via-purple-500/20 group-hover:to-pink-500/20" />
            {loading
              ? isDetailsStep
                ? "Submitting..."
                : "Verifying..."
              : isDetailsStep
              ? "Continue (send OTP to admin)"
              : "Verify OTP & create account"}
          </button>

          {/* Footer */}
          <p className="mt-4 text-sm text-center text-gray-700">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-blue-700 hover:underline"
            >
              Log in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
